import { Button, Dialog, DialogActions, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';
import { useUpdateContractByBuyerMutation } from '../mutations/__generated__/updateContractByBuyer.generated';
import { ContractsAsBuyerDocument } from '../queries/__generated__/contractsAsBuyer.generated';
import { ContractsAsSupplierDocument } from '../queries/__generated__/contractsAsSupplier.generated';
import { commonSnackbarPropsAtom } from '../common/state/state';
import { ContractPreview } from '../hooks/useContracts';

interface DeactivateTrustedSupplierConfirmDialogProps {
  contract: ContractPreview;
  open: boolean;
  onClose: () => void;
}

export default function DeactivateTrustedSupplierConfirmDialog({ open, contract, onClose }: DeactivateTrustedSupplierConfirmDialogProps): React.JSX.Element {
  const { t } = useTranslation(['contracts']);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);

  const [updateContract] = useUpdateContractByBuyerMutation({
    refetchQueries: [ContractsAsBuyerDocument, ContractsAsSupplierDocument],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data.updateContract?.contractId) {
        setCommonSnackbarProps({
          label: t('contracts:trusted_supplier:trusted_partnership_deactivated'),
          snackbarProps: { open: true },
        });
      }
    },
  });

  const handleDeactivate = (): void => {
    updateContract({
      variables: {
        data: {
          contractId: contract.id,
          companyId: contract.buyingCompanyId,
          contractInput: {
            isTrustedSupplier: false,
          },
        },
      },
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Typography>{t('contracts:trusted_supplier.deactivate_trusted_partnership')}</Typography>
        <Typography>{t('contracts:trusted_supplier.deactivate_partnership_description')}</Typography>
      </DialogTitle>
      <DialogActions
        sx={{
          flexDirection: {
            xs: 'column-reverse',
            sm: 'row',
          },
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            width: {
              xs: '100%',
              sm: 'fit-content',
            },
          }}
          variant="outlined"
        >
          {t('contracts:trusted_supplier.no_deactivate_partnership')}
        </Button>
        <Button
          variant="contained"
          onClick={handleDeactivate}
          sx={{
            width: {
              xs: '100%',
              sm: 'fit-content',
            },
          }}
        >
          {t('contracts:trusted_supplier.yes_keep_partnership')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
